import type { RunRow } from "@/types/run"

/**
 * The whole point of the history screen: why "sent" is smaller than "matched".
 *
 * The engine puts every member in exactly one bucket, so matched is always the
 * sum of the six — the tiles and the sentence beneath them can never disagree.
 */
export function RunCounts({ run }: { run: RunRow }) {
  return (
    <div>
      <div className="grid grid-cols-2 gap-3 xs:grid-cols-3 md:grid-cols-7">
        <Stat label="Matched" value={run.matched_count} emphasis />
        <Stat label="Sent" value={run.sent_count} emphasis />
        <Stat label="Failed" value={run.failed_count} danger={run.failed_count > 0} />
        <Stat label="Opted out" value={run.opted_out_count} />
        <Stat label="No mobile" value={run.no_mobile_count} />
        <Stat label="Already texted" value={run.duplicate_count} />
        <Stat label="Too long" value={run.too_long_count} />
      </div>
      <Explanation run={run} />
    </div>
  )
}

/** "10,576 matched, minus 429 opted out — 10,147 texted." */
function Explanation({ run }: { run: RunRow }) {
  if (run.status === "refused") return null

  const reasons = [
    run.failed_count > 0 && `${run.failed_count.toLocaleString()} the service rejected`,
    run.opted_out_count > 0 && `${run.opted_out_count.toLocaleString()} opted out`,
    run.no_mobile_count > 0 &&
      `${run.no_mobile_count.toLocaleString()} with no mobile number`,
    run.duplicate_count > 0 &&
      `${run.duplicate_count.toLocaleString()} already texted that day`,
    run.too_long_count > 0 &&
      `${run.too_long_count.toLocaleString()} whose text came out too long to send`,
  ].filter(Boolean) as string[]

  if (reasons.length === 0) {
    return (
      <p className="mt-3 text-xs text-ink-muted">
        {run.matched_count.toLocaleString()} matched, and every one of them was
        texted.
      </p>
    )
  }

  return (
    <p className="mt-3 text-xs text-ink-muted">
      {run.matched_count.toLocaleString()} matched, minus{" "}
      {joinWithAnd(reasons)} — {run.sent_count.toLocaleString()} texted.
    </p>
  )
}

function joinWithAnd(parts: string[]) {
  if (parts.length === 1) return parts[0]
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
}

function Stat({
  label,
  value,
  emphasis,
  danger,
}: {
  label: string
  value: number
  emphasis?: boolean
  danger?: boolean
}) {
  return (
    <div className="rounded-md border border-hairline bg-page p-3">
      <div className="text-xs text-ink-muted">{label}</div>
      <div
        className={[
          "mt-1 tabular-nums",
          emphasis ? "text-xl font-semibold" : "text-lg font-medium",
          danger ? "text-danger-display" : "text-ink-display",
        ].join(" ")}
      >
        {value.toLocaleString()}
      </div>
    </div>
  )
}
